<template>
  <v-form
    ref="form"
    v-model="valid"
    class="customer-order-form"
    @input="$emit('valid', $event)"
  >
    <v-row>
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <v-sheet
              class="pa-4"
              color="grey lighten-4 text-subtitle-1"
              rounded
            >
              <strong class="mr-1">{{ $t('ORDERS.DATE_ENTERED') }}:</strong>
              <span>{{ form.date_entered | dateFormat }}</span>
            </v-sheet>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <order-transition-status
              v-model="form.status_id"
              :order-id="form.id"
              name="status_id"
              :action="action"
              :readonly="readonly"
              :disabled="!readonly && !editable"
              :label="`${$t('ORDERS.ORDER_STATUS')} *`"
              :error-messages="errors.status_id"
              @change="updateValue('status_id', $event)"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <planning-btn
              v-if="readonly"
              :day="form.date_service_expected"
              :order="form.id"
              :disabled="disabledPlanningButton"
              :error-messages="planningButtonErrorMessages"
              color="primary"
              block
              depressed
            ></planning-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <order-customer-details
              class="pa-4"
              :type="form.type_id"
              :customer="person"
              show-title
              rounded
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <building-complex-details
              :inhabitant="inhabitant"
              class="pa-4"
              rounded
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col v-if="!readonly && !editable">
            <v-select
              v-model="form.service_id"
              name="service_id"
              item-value="id"
              item-text="title"
              :items="services"
              :readonly="readonly || editable"
              :rules="rules.service_id"
              :label="`${$t('SERVICES.ITEM')} *`"
              :error-messages="errors.service_id"
              @change="setService"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <order-service-details
              :service="selectedService"
              class="pa-4"
              rounded
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-switch
              v-model="form.is_tender"
              name="is_tender"
              :label="$t('ORDERS.TENDER')"
              :readonly="readonly"
              :error="errors.is_tender"
              @change="updateValue('is_tender', $event)"
            ></v-switch>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <h3 class="title text--primary">
              {{ $t('ORDERS.SERVICE_DETAILS') }}:
            </h3>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <money-input
              v-model="form.price"
              name="price"
              :counter="5"
              :counter-value="countNumberPrice"
              :readonly="readonly"
              :rules="rules.price"
              :label="$t('ITEM.PRICE')"
              :error-messages="errors.price"
              @input="updateValue('price', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model.number="form.duration"
              name="duration"
              type="number"
              min="0"
              step="0.25"
              :suffix="$t('ITEM.HOURS')"
              :counter="4"
              :counter-value="countNumber"
              :readonly="readonly"
              :rules="rules.duration"
              :label="$t('ORDERS.DURATION')"
              :error-messages="errors.duration"
              @input="updateValue('duration', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-if="readonly"
              :value="form.date_service_expected | dateFormat"
              :label="$t('ORDERS.DATE_SERVICE_EXPECTED')"
              :error-messages="errors.date_service_expected"
              prepend-inner-icon="mdi-calendar"
              readonly
            ></v-text-field>

            <v-menu
              v-else
              ref="menuDateServiceExpected"
              min-width="290px"
              transition="scale-transition"
              :close-on-content-click="false"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  autocomplete="nope"
                  prepend-inner-icon="mdi-calendar"
                  :value="form.date_service_expected | dateFormat"
                  :label="$t('ORDERS.DATE_SERVICE_EXPECTED')"
                  :readonly="readonly"
                  :error-messages="errors.date_service_expected"
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="form.date_service_expected = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.date_service_expected"
                :min="minDateServiceExpected"
                first-day-of-week="1"
                show-week
                @change="setDateServiceExpected"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-select
              v-model="form.substitution_options"
              name="substitution_options"
              item-value="id"
              item-text="name"
              :items="substitutionOptions"
              :readonly="readonly"
              :label="$t('ORDERS.SUBSTITUTION_OPTIONS')"
              multiple
              small-chips
              :error-messages="errors.substitution_options"
              @change="updateValue('substitution_options', $event)"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <order-sub-questions :text="form.sub_questions" />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-textarea
              v-model="form.description"
              name="description"
              :counter="500"
              :readonly="readonly"
              :rules="rules.description"
              :label="$t('ITEM.DESCRIPTION')"
              :error-messages="errors.description"
              outlined
              @input="updateValue('description', $event)"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-col>
      <v-divider vertical />
      <v-col cols="12" md="4">
        <v-row dense>
          <v-col>
            <h3 class="title text--primary">
              {{ $t('ORDERS.SERVICE_ADDRESS') }}:
            </h3>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model="form.street"
              name="street"
              :counter="100"
              :rules="rules.street"
              :readonly="readonly"
              :label="`${$t('PROFILE.STREET')} *`"
              :error-messages="errors.street"
              @change="updateValue('street', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <zip-code-input
              v-model="form.zip"
              :readonly="readonly"
              :error-messages="errors.zip"
              :rules="rules.zip"
              :label="`${$t('PROFILE.ZIP')} *`"
              @input="updateValue('zip', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-text-field
              v-model="form.city"
              name="city"
              :counter="40"
              :readonly="readonly"
              :rules="rules.city"
              :label="`${$t('PROFILE.CITY')} *`"
              :error-messages="errors.city"
              @change="updateValue('city', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              v-model="form.apartment"
              name="apartment"
              :counter="40"
              :readonly="readonly"
              :rules="rules.apartment"
              :label="$t('APARTMENTS.ITEM')"
              :error-messages="errors.apartment"
              @input="updateValue('apartment', $event)"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.floor"
              name="floor"
              :rules="rules.floor"
              :counter="20"
              :label="$t('APARTMENTS.FLOOR')"
              :readonly="readonly"
              :error-messages="errors.floor"
              @input="updateValue('floor', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <order-region
              v-model="form.region_id"
              :zip="form.zip"
              :readonly="readonly"
              :error-messages="errors.region_id"
              @input="updateValue('region_id', $event)"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-textarea
              v-model="form.key_deposited"
              name="key_deposited"
              :counter="100"
              outlined
              :readonly="readonly"
              :rules="rules.key_deposited"
              :label="$t('ORDERS.KEY_DEPOSITED')"
              :error-messages="errors.key_deposited"
              @input="updateValue('key_deposited', $event)"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <slot name="buttons" :valid="valid" />
    </v-row>
  </v-form>
</template>
<script>
import { dateFormat } from '@/modules/_global/filters';
import { mapActions } from 'vuex';
import formEditable from '@/modules/_global/mixins/form-editable';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { CUSTOMER } from './../../common/order-types';
import orderForm from '../forms/mixins/orderForm';
import { mapCacheActions } from 'vuex-cache';
import OrderRegion from '@/modules/orders/components/forms/OrderRegion.vue';
import BuildingComplexDetails from '@/modules/orders/components/BuildingComplexDetails';
import OrderSubQuestions from '@/modules/orders/components/forms/OrderSubQuestions';
import ZipCodeInput from '@/modules/_global/_components/zip/ZipCodeInput';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';
import OrderTransitionStatus from '@/modules/orders/components/forms/OrderTransitionStatus.vue';

export default {
  name: 'OrderCustomerForm',

  components: {
    OrderTransitionStatus,
    MoneyInput,
    ZipCodeInput,
    OrderSubQuestions,
    BuildingComplexDetails,
    OrderRegion
  },

  filters: {
    dateFormat
  },

  mixins: [orderForm, formEditable, i18nRefreshData],

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    person: {
      type: Object,
      default: () => ({
        category: [],
        mobile_phone: '',
        phone: '',
        email: '',
        first_name: '',
        last_name: '',
        building_complex: {}
      })
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      form: {
        order_number: '',
        date_entered: '',
        street: '',
        zip: '',
        city: '',
        apartment: '',
        key_deposited: '',
        price: '',
        description: '',
        sub_questions: '',
        duration: '',
        date_service_expected: '',
        substitution_options: null,
        is_tender: true,
        service_id: null,
        status_id: 2,
        type_id: CUSTOMER,
        region_id: null
      },
      substitutionOptions: []
    };
  },

  computed: {
    inhabitant() {
      return this.person && this.person.inhabitant;
    }
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapActions('service', ['retrieveServicesCCustomer']),
    ...mapCacheActions('order', ['getOrderSubstitutionOptions']),
    refreshData() {
      this.getOrderSubstitutionOptions().then(({ data }) => {
        this.substitutionOptions = data.data;
      });

      this.retrieveServicesCCustomer().then(({ data }) => {
        this.services = data;
      });
    }
  }
};
</script>

<style lang="scss">
.customer-order-form {
  .row {
    > .col,
    > [class*='col-'] {
      padding-top: 4px;
      padding-bottom: 4px;
    }
  }
  .min-height .v-input__slot {
    min-height: 218px;
  }
}
</style>
